import { createPlayerEvents } from "./playerEvents.js"
import { intendedPosition } from "./programmaticSeek.js"
import { trackDuration } from "./trackContract.js"
import { createTrackSwitcher } from "./trackSwitcher.js"

/**
 * SC-1323: PlayerShell's statistics and switching wiring, as plain JS so the golden harness
 * (tests/js/golden/harness.js shellHarness) runs exactly the code the shell runs. The shell keeps
 * everything visual; this owns the playerEvents binding, the per-event viewing state, the track
 * switcher and the mode it is in. Tracks are fixed for the shell's life.
 */
export function createShellWiring({ tracks, sameTimeline, initialMode, emit, isFullscreen = () => false, levels = () => [], currentLevel = () => -1, resumeFor = () => 0 }) {
    const videoTrack = tracks.find((track) => track.kind === "video") ?? null
    const audioTrack = tracks.find((track) => track.kind === "audio") ?? null
    const unbinders = []
    let mode = initialMode
    let elements = { video: null, audio: null }
    let attached = false
    let switcher = null

    // Per-event viewing state, pulled in lazily so the position stream stays cheap. quality_height is
    // the current rendition; viewport reflects the rendering surface at the moment of the event.
    function eventState(el) {
        const state = { fullscreen: isFullscreen() }
        const level = levels()?.[currentLevel()]

        if (level && typeof level.height === "number") {
            state.qualityHeight = level.height
        }

        if (el) {
            state.viewport = { width: el.clientWidth || 0, height: el.clientHeight || 0 }
        }

        return state
    }

    const events = createPlayerEvents({ emit, stateFor: eventState })
    const activeTrack = () => (mode === "audio" ? audioTrack : videoTrack) ?? videoTrack ?? audioTrack
    const elementFor = (track) => (track?.kind === "audio" ? elements.audio : elements.video)

    function reportQualitySwitch() {
        if (attached && videoTrack && elements.video) {
            events.qualitySwitch(videoTrack, elements.video)
        }
    }

    function detach() {
        unbinders.splice(0).forEach((unbind) => unbind())
        switcher = null
        elements = { video: null, audio: null }
        attached = false
    }

    return {
        /**
         * Idempotent: a second attach first detaches, so element events are never bound twice
         * (a double binding would double every live podcast beacon).
         *
         * Still, attach exactly once per shell (PlayerShell's onMounted does): a re-attach builds a
         * new track switcher, and in audio mode that re-assigns audio.src and restarts the audio.
         * Pages (VideoPlayer.vue, PortalPlayer.vue) never call it themselves.
         */
        attach({ controller = null, video = null, audio = null, playback } = {}) {
            detach()
            elements = { video, audio }
            attached = true

            for (const [el, track] of [
                [video, videoTrack],
                [audio, audioTrack],
            ]) {
                if (el && track) {
                    unbinders.push(events.bind(el, track))
                }
            }

            if (video && audio && videoTrack && audioTrack) {
                switcher = createTrackSwitcher({
                    controller,
                    video,
                    audio,
                    tracks,
                    sameTimeline,
                    initialMode: mode,
                    playback,
                    resumeFor,
                    onSwitchPause: (el) => events.markSwitchPause(el),
                })
            }
        },
        // After detach every call fails closed: no element is referenced, nothing is emitted.
        detach,
        mode: () => mode,
        activeTrack,
        elementFor,
        /**
         * Runs inside the toggle's click handler: everything up to the switcher's play() is
         * synchronous (no await, no nextTick), so iOS keeps the user gesture.
         */
        switchMode(next, { play = true, report = true } = {}) {
            if (!switcher || next === mode) {
                return null
            }

            const fromTrack = activeTrack()
            const fromEl = elementFor(fromTrack)
            const fromPosition = fromEl ? intendedPosition(fromEl) : 0
            const fromDuration = fromEl && Number.isFinite(fromEl.duration) && fromEl.duration > 0 ? fromEl.duration : trackDuration(fromTrack) || null
            const result = switcher.switchTo(next, { play })

            if (result === null) {
                return null
            }

            mode = next

            if (report) {
                events.modeSwitch({ fromTrack, toTrack: result.toTrack, fromPosition, fromDuration, fromEl })
            }

            return result
        },
        ctaClick(cta) {
            if (!attached) {
                return
            }

            const track = activeTrack()

            events.ctaClick(track, elementFor(track), cta)
        },
        // hls.js LEVEL_SWITCHED → currentLevel changes. One quality_switch per real change; the
        // initial -1 → 0 transition on the first manifest parse is not one.
        qualityChanged(newLevel, oldLevel) {
            if (newLevel === oldLevel || newLevel < 0 || oldLevel < 0 || !levels()?.[newLevel]) {
                return
            }

            reportQualitySwitch()
        },
        reportQualitySwitch,
        // document.fullscreenchange fires for every transition; the fullscreen events describe video
        // viewing, so audio mode reports none.
        fullscreenChanged() {
            if (!attached || mode === "audio" || !videoTrack || !elements.video) {
                return
            }

            events.fullscreen(videoTrack, elements.video, isFullscreen())
        },
    }
}
